// Per-dialect synthesizer for view + materialized-view ops (v2 Slice D2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D2.
//
// Per-engine surface:
//   - mssql:  CREATE [OR ALTER] VIEW <name> [WITH SCHEMABINDING] AS <body>
//             DROP VIEW [IF EXISTS] <name>
//             materialized views → unsupported (use indexed views — separate)
//   - pgsql:  CREATE [OR REPLACE] VIEW <name> [WITH (option = …)] AS <body>
//             CREATE MATERIALIZED VIEW [IF NOT EXISTS] <name> AS <body>
//                  [WITH {DATA|NO DATA}]
//             REFRESH MATERIALIZED VIEW [CONCURRENTLY] <name> [WITH …]
//             DROP [MATERIALIZED] VIEW [IF EXISTS] <name> [CASCADE]
//   - mysql:  CREATE [OR REPLACE] [ALGORITHM = …] [DEFINER = …]
//                  [SQL SECURITY {DEFINER|INVOKER}] VIEW <name>
//                  AS <body> [WITH {LOCAL|CASCADED} CHECK OPTION]
//             DROP VIEW [IF EXISTS] <name>
//             materialized views → unsupported
//   - oracle: CREATE [OR REPLACE] [EDITIONABLE|NONEDITIONABLE] [FORCE]
//                  VIEW <name> AS <body>
//             CREATE MATERIALIZED VIEW <name>
//                  [REFRESH {COMPLETE|FAST|FORCE} [ON {DEMAND|COMMIT}]]
//                  AS <body>
//             DROP [MATERIALIZED] VIEW <name> [CASCADE CONSTRAINTS]
//   - sqlite: CREATE [TEMPORARY] VIEW <name> AS <body>
//             DROP VIEW [IF EXISTS] <name>
//             materialized views / refresh → unsupported
//
// `bodyText` is the verbatim SELECT body — the synthesizer emits ` AS <body>`
// without re-parsing. Engines that don't support an op throw with a documented
// redirect (mssql/mysql/sqlite materialized → not supported; mssql refresh →
// no equivalent; sqlite OR REPLACE / OR ALTER → DROP+CREATE workaround).

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { MaterializedViewDef, ViewDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function viewName(engine: SqlEngine, name: QualifiedName): string {
  if (name.raw && name.raw.length > 0) return name.raw;
  const bare = maybeQuoteIdent(engine, name.name, name.quoted);
  if (name.schema) {
    const schema = maybeQuoteIdent(engine, name.schema, name.quoted);
    return `${schema}.${bare}`;
  }
  return bare;
}

function rejectField(
  engine: SqlEngine,
  op: string,
  field: string,
): never {
  throw new Error(
    `applyDdlEdit: ${engine} ${op} does not accept '${field}'`,
  );
}

function unsupported(engine: SqlEngine, op: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${hint}`,
  );
}

function validateBody(view: { bodyText: string }, op: string): void {
  if (view.bodyText.trim().length === 0) {
    throw new Error(
      `applyDdlEdit: ${op} requires a non-empty bodyText (the SELECT body)`,
    );
  }
}

/** Emit `(col1, col2)` after the view name. All five engines accept this
 *  verbatim. Whitespace-only entries are dropped; columns themselves are
 *  emitted as-is (caller supplies engine-correct quoting). Returns the empty
 *  string when no columns or all entries are blank. */
function formatViewColumns(columns: readonly string[] | undefined): string {
  if (!columns || columns.length === 0) return '';
  const parts = columns.map((c) => c.trim()).filter((c) => c.length > 0);
  if (parts.length === 0) return '';
  return ` (${parts.join(', ')})`;
}

// ─── CREATE VIEW ──────────────────────────────────────────────────────────

export function synthesizeCreateView(
  engine: SqlEngine,
  view: ViewDef,
): string {
  validateBody(view, 'create-view');
  if (engine === 'mssql') return synthMssqlView(view);
  if (engine === 'postgres') return synthPgsqlView(view);
  if (engine === 'mysql') return synthMysqlView(view);
  if (engine === 'oracle') return synthOracleView(view);
  return synthSqliteView(view);
}

function synthMssqlView(view: ViewDef): string {
  if (view.orReplace) {
    rejectField('mssql', 'create-view', 'orReplace');
  }
  rejectNonMssqlViewFields('mssql', view);
  let s = view.mssqlOrAlter ? 'CREATE OR ALTER VIEW ' : 'CREATE VIEW ';
  s += viewName('mssql', view.name);
  s += formatViewColumns(view.columns);
  if (view.mssqlWithSchemabinding) s += ' WITH SCHEMABINDING';
  s += ` AS ${view.bodyText.trim()}`;
  return s;
}

function synthPgsqlView(view: ViewDef): string {
  if (view.mssqlOrAlter) rejectField('postgres', 'create-view', 'mssqlOrAlter');
  if (view.mssqlWithSchemabinding) {
    rejectField('postgres', 'create-view', 'mssqlWithSchemabinding');
  }
  rejectMysqlOnlyFields('postgres', view);
  rejectOracleOnlyFields('postgres', view);
  rejectSqliteOnlyFields('postgres', view);
  let s = view.orReplace ? 'CREATE OR REPLACE VIEW ' : 'CREATE VIEW ';
  s += viewName('postgres', view.name);
  s += formatViewColumns(view.columns);
  if (view.pgsqlOptions) {
    const parts: string[] = [];
    if (view.pgsqlOptions.securityBarrier !== undefined) {
      parts.push(`security_barrier = ${view.pgsqlOptions.securityBarrier}`);
    }
    if (view.pgsqlOptions.checkOption !== undefined) {
      parts.push(`check_option = ${view.pgsqlOptions.checkOption}`);
    }
    if (parts.length > 0) s += ` WITH (${parts.join(', ')})`;
  }
  s += ` AS ${view.bodyText.trim()}`;
  return s;
}

function synthMysqlView(view: ViewDef): string {
  if (view.mssqlOrAlter) rejectField('mysql', 'create-view', 'mssqlOrAlter');
  if (view.mssqlWithSchemabinding) {
    rejectField('mysql', 'create-view', 'mssqlWithSchemabinding');
  }
  if (view.pgsqlOptions) rejectField('mysql', 'create-view', 'pgsqlOptions');
  rejectOracleOnlyFields('mysql', view);
  rejectSqliteOnlyFields('mysql', view);
  let s = view.orReplace ? 'CREATE OR REPLACE ' : 'CREATE ';
  if (view.mysqlAlgorithm) {
    s += `ALGORITHM = ${view.mysqlAlgorithm.toUpperCase()} `;
  }
  if (view.mysqlDefiner) s += `DEFINER = ${view.mysqlDefiner} `;
  if (view.mysqlSqlSecurity) {
    s += `SQL SECURITY ${view.mysqlSqlSecurity.toUpperCase()} `;
  }
  s += `VIEW ${viewName('mysql', view.name)}`;
  s += formatViewColumns(view.columns);
  s += ` AS ${view.bodyText.trim()}`;
  if (view.mysqlCheckOption) {
    s += ` WITH ${view.mysqlCheckOption.toUpperCase()} CHECK OPTION`;
  }
  return s;
}

function synthOracleView(view: ViewDef): string {
  if (view.mssqlOrAlter) rejectField('oracle', 'create-view', 'mssqlOrAlter');
  if (view.mssqlWithSchemabinding) {
    rejectField('oracle', 'create-view', 'mssqlWithSchemabinding');
  }
  if (view.pgsqlOptions) rejectField('oracle', 'create-view', 'pgsqlOptions');
  rejectMysqlOnlyFields('oracle', view);
  rejectSqliteOnlyFields('oracle', view);
  if (view.oracleEditionable && view.oracleNonEditionable) {
    throw new Error(
      `applyDdlEdit: oracle create-view: 'oracleEditionable' and `
        + `'oracleNonEditionable' are mutually exclusive`,
    );
  }
  let s = view.orReplace ? 'CREATE OR REPLACE ' : 'CREATE ';
  if (view.oracleEditionable) s += 'EDITIONABLE ';
  if (view.oracleNonEditionable) s += 'NONEDITIONABLE ';
  if (view.oracleForce) s += 'FORCE ';
  s += `VIEW ${viewName('oracle', view.name)}`;
  s += formatViewColumns(view.columns);
  s += ` AS ${view.bodyText.trim()}`;
  return s;
}

function synthSqliteView(view: ViewDef): string {
  if (view.orReplace) {
    unsupported('sqlite', 'create-view orReplace',
      `sqlite has no atomic CREATE OR REPLACE VIEW. Drop and recreate the `
        + `view in two steps: 'DROP VIEW IF EXISTS <name>' then 'CREATE VIEW '.`);
  }
  if (view.mssqlOrAlter) rejectField('sqlite', 'create-view', 'mssqlOrAlter');
  if (view.mssqlWithSchemabinding) {
    rejectField('sqlite', 'create-view', 'mssqlWithSchemabinding');
  }
  if (view.pgsqlOptions) rejectField('sqlite', 'create-view', 'pgsqlOptions');
  rejectMysqlOnlyFields('sqlite', view);
  rejectOracleOnlyFields('sqlite', view);
  let s = view.sqliteTemporary ? 'CREATE TEMPORARY VIEW ' : 'CREATE VIEW ';
  s += viewName('sqlite', view.name);
  s += formatViewColumns(view.columns);
  s += ` AS ${view.bodyText.trim()}`;
  return s;
}

function rejectNonMssqlViewFields(engine: SqlEngine, view: ViewDef): void {
  if (view.pgsqlOptions) rejectField(engine, 'create-view', 'pgsqlOptions');
  rejectMysqlOnlyFields(engine, view);
  rejectOracleOnlyFields(engine, view);
  rejectSqliteOnlyFields(engine, view);
}

function rejectMysqlOnlyFields(engine: SqlEngine, view: ViewDef): void {
  if (view.mysqlAlgorithm) rejectField(engine, 'create-view', 'mysqlAlgorithm');
  if (view.mysqlDefiner) rejectField(engine, 'create-view', 'mysqlDefiner');
  if (view.mysqlSqlSecurity) {
    rejectField(engine, 'create-view', 'mysqlSqlSecurity');
  }
  if (view.mysqlCheckOption) {
    rejectField(engine, 'create-view', 'mysqlCheckOption');
  }
}

function rejectOracleOnlyFields(engine: SqlEngine, view: ViewDef): void {
  if (view.oracleEditionable) {
    rejectField(engine, 'create-view', 'oracleEditionable');
  }
  if (view.oracleNonEditionable) {
    rejectField(engine, 'create-view', 'oracleNonEditionable');
  }
  if (view.oracleForce) rejectField(engine, 'create-view', 'oracleForce');
}

function rejectSqliteOnlyFields(engine: SqlEngine, view: ViewDef): void {
  if (view.sqliteTemporary) {
    rejectField(engine, 'create-view', 'sqliteTemporary');
  }
}

// ─── DROP VIEW ────────────────────────────────────────────────────────────

export function synthesizeDropView(
  engine: SqlEngine,
  name: QualifiedName,
  ifExists: boolean,
  cascade: boolean,
): string {
  if (cascade && (engine === 'mssql' || engine === 'mysql' || engine === 'sqlite')) {
    rejectField(engine, 'drop-view', 'cascade');
  }
  let s = 'DROP VIEW ';
  if (ifExists) s += 'IF EXISTS ';
  s += viewName(engine, name);
  // Oracle's DROP VIEW only accepts CASCADE CONSTRAINTS (never bare CASCADE);
  // postgres accepts bare CASCADE.
  if (cascade) s += engine === 'oracle' ? ' CASCADE CONSTRAINTS' : ' CASCADE';
  return s;
}

// ─── CREATE MATERIALIZED VIEW ─────────────────────────────────────────────

export function synthesizeCreateMaterializedView(
  engine: SqlEngine,
  view: MaterializedViewDef,
): string {
  validateBody(view, 'create-materialized-view');
  if (engine === 'mssql') {
    unsupported('mssql', 'create-materialized-view',
      `mssql has no CREATE MATERIALIZED VIEW. The closest equivalent is an `
        + `indexed view: CREATE VIEW … WITH SCHEMABINDING + CREATE UNIQUE `
        + `CLUSTERED INDEX (use op:'create-view' with `
        + `mssqlWithSchemabinding: true followed by op:'add-index').`);
  }
  if (engine === 'mysql') {
    unsupported('mysql', 'create-materialized-view',
      `mysql has no materialized views — there is no portable workaround. `
        + `Consider a regular view (op:'create-view') over precomputed table `
        + `data refreshed via INSERT…SELECT.`);
  }
  if (engine === 'sqlite') {
    unsupported('sqlite', 'create-materialized-view',
      `sqlite has no materialized views. Use a CREATE TABLE AS SELECT for a `
        + `materialized snapshot, or a regular view (op:'create-view') for a `
        + `live query.`);
  }
  if (engine === 'postgres') return synthPgsqlMaterializedView(view);
  return synthOracleMaterializedView(view);
}

function synthPgsqlMaterializedView(view: MaterializedViewDef): string {
  if (view.orReplace) {
    rejectField('postgres', 'create-materialized-view', 'orReplace');
  }
  if (view.oracleRefreshMode) {
    rejectField('postgres', 'create-materialized-view', 'oracleRefreshMode');
  }
  if (view.oracleRefreshOn) {
    rejectField('postgres', 'create-materialized-view', 'oracleRefreshOn');
  }
  let s = 'CREATE MATERIALIZED VIEW ';
  if (view.ifNotExists) s += 'IF NOT EXISTS ';
  s += viewName('postgres', view.name);
  s += ` AS ${view.bodyText.trim()}`;
  if (view.pgsqlWithData === true) s += ' WITH DATA';
  else if (view.pgsqlWithData === false) s += ' WITH NO DATA';
  return s;
}

function synthOracleMaterializedView(view: MaterializedViewDef): string {
  if (view.ifNotExists) {
    rejectField('oracle', 'create-materialized-view', 'ifNotExists');
  }
  if (view.pgsqlWithData !== undefined) {
    rejectField('oracle', 'create-materialized-view', 'pgsqlWithData');
  }
  let s = view.orReplace
    ? 'CREATE OR REPLACE MATERIALIZED VIEW '
    : 'CREATE MATERIALIZED VIEW ';
  s += viewName('oracle', view.name);
  if (view.oracleRefreshMode) {
    s += ` REFRESH ${view.oracleRefreshMode.toUpperCase()}`;
    if (view.oracleRefreshOn) {
      s += ` ON ${view.oracleRefreshOn.toUpperCase()}`;
    }
  } else if (view.oracleRefreshOn) {
    throw new Error(
      `applyDdlEdit: oracle create-materialized-view: 'oracleRefreshOn' `
        + `requires 'oracleRefreshMode' to also be set`,
    );
  }
  s += ` AS ${view.bodyText.trim()}`;
  return s;
}

// ─── DROP MATERIALIZED VIEW ───────────────────────────────────────────────

export function synthesizeDropMaterializedView(
  engine: SqlEngine,
  name: QualifiedName,
  ifExists: boolean,
  cascade: boolean,
): string {
  if (engine === 'mssql' || engine === 'mysql' || engine === 'sqlite') {
    unsupported(engine, 'drop-materialized-view',
      `${engine} has no materialized views — see op:'create-materialized-view' `
        + `for engine-specific workarounds.`);
  }
  // Oracle's DROP MATERIALIZED VIEW has no cascade clause at all (unlike
  // DROP VIEW, which accepts CASCADE CONSTRAINTS). Postgres accepts CASCADE.
  if (cascade && engine === 'oracle') {
    rejectField('oracle', 'drop-materialized-view', 'cascade');
  }
  let s = 'DROP MATERIALIZED VIEW ';
  if (ifExists) s += 'IF EXISTS ';
  s += viewName(engine, name);
  if (cascade) s += ' CASCADE';
  return s;
}

// ─── REFRESH MATERIALIZED VIEW ────────────────────────────────────────────

export function synthesizeRefreshMaterializedView(
  engine: SqlEngine,
  name: QualifiedName,
  concurrently: boolean,
  pgsqlWithData: boolean | undefined,
): string {
  if (engine !== 'postgres') {
    unsupported(engine, 'refresh-materialized-view',
      engine === 'oracle'
        ? `oracle's refresh idiom is procedural — call `
          + `DBMS_MVIEW.REFRESH('<name>', '<method>') from PL/SQL or use `
          + `the REFRESH clause on op:'create-materialized-view'.`
        : `${engine} has no materialized views.`);
  }
  let s = 'REFRESH MATERIALIZED VIEW ';
  if (concurrently) s += 'CONCURRENTLY ';
  s += viewName(engine, name);
  if (pgsqlWithData === true) s += ' WITH DATA';
  else if (pgsqlWithData === false) s += ' WITH NO DATA';
  return s;
}
